import * as React from 'react'
import { FaIcon, faXmark, faPrint } from '@/lib/fa'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'

export type ViolationPrintReport =
  | 'violations'
  | 'violationsWithActivities'
  | 'groupedViolations'

interface ViolationsPrintModalProps {
  isOpen: boolean
  pending?: boolean
  onClose: () => void
  onGenerate: (selectedReports: ViolationPrintReport[]) => void
}

type ReportOption = {
  id: ViolationPrintReport
  labelKey: string
}

const REPORT_OPTIONS: ReportOption[] = [
  {
    id: 'violations',
    labelKey: 'printout.violations',
  },
  {
    id: 'violationsWithActivities',
    labelKey: 'printout.violationsWithActivities',
  },
  {
    id: 'groupedViolations',
    labelKey: 'printout.groupedViolations',
  },
]

const ViolationsPrintModal: React.FC<ViolationsPrintModalProps> = ({
  isOpen,
  pending,
  onClose,
  onGenerate,
}) => {
  const { t } = useLocalize()
  const [selectedReports, setSelectedReports] = React.useState<
    Set<ViolationPrintReport>
  >(() => new Set())

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedReports(new Set())
    }
  }, [isOpen])

  const handleReportToggle = (reportId: ViolationPrintReport) => {
    setSelectedReports((prev) => {
      const updated = new Set(prev)
      if (updated.has(reportId)) {
        updated.delete(reportId)
      } else {
        updated.add(reportId)
      }
      return updated
    })
  }

  const handleSelectAll = () => {
    if (selectedReports.size === REPORT_OPTIONS.length) {
      setSelectedReports(new Set())
    } else {
      setSelectedReports(new Set(REPORT_OPTIONS.map((option) => option.id)))
    }
  }

  const handleGenerate = () => {
    if (selectedReports.size === 0 || pending) {
      return
    }
    onGenerate(Array.from(selectedReports))
  }

  if (!isOpen) return null

  return (
    <ModalLayout customZIndex={9999}>
      <div className="tw-flex tw-w-[615px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white">
        <div className="tw-flex tw-h-[64px] tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-px-6">
          <h2 className="tw-text-xl tw-font-semibold tw-leading-[28px] tw-text-[#1f2937]">
            {t('printout.chooseReport')}
          </h2>
          <button
            onClick={onClose}
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100"
            aria-label={t('common.close')}
          >
            <FaIcon
              icon={faXmark}
              className="tw-h-5 tw-w-5 tw-text-[#1f2937]"
            />
          </button>
        </div>

        <div className="tw-overflow-y-auto">
          <div className="tw-sticky tw-top-0 tw-z-10 tw-grid tw-grid-cols-[40px_1fr] tw-border-b tw-border-[#e5e6e6] tw-bg-[#f2f2f2] tw-bg-opacity-25">
            <div className="tw-flex tw-h-8 tw-items-center tw-justify-center tw-p-2">
              <Checkbox
                checked={
                  selectedReports.size === REPORT_OPTIONS.length &&
                  REPORT_OPTIONS.length > 0
                }
                onChange={handleSelectAll}
                className="tw-h-4 tw-w-4"
              />
            </div>
            <div className="tw-flex tw-h-8 tw-items-center tw-gap-2 tw-px-3 tw-py-2">
              <span className="tw-text-xs tw-font-medium tw-leading-[14px] tw-text-[#1f2937]">
                {t('printout.report-name')}
              </span>
            </div>
          </div>

          <div className="tw-divide-y tw-divide-[#e5e6e6]">
            {REPORT_OPTIONS.map((option) => {
              const isSelected = selectedReports.has(option.id)
              const handleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
                const target = e.target as HTMLElement
                if (
                  target.tagName === 'INPUT' ||
                  target.closest('input') ||
                  target.closest('label')
                ) {
                  return
                }
                handleReportToggle(option.id)
              }
              return (
                <div
                  key={option.id}
                  onClick={handleRowClick}
                  className="tw-grid tw-h-10 tw-cursor-pointer tw-grid-cols-[40px_1fr] tw-border-b tw-border-[#e5e6e6] hover:tw-bg-gray-50"
                >
                  <div className="tw-flex tw-items-center tw-justify-center tw-p-2">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleReportToggle(option.id)
                      }}
                      className="tw-h-4 tw-w-4"
                    />
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-border-r tw-border-[#e5e6e6] tw-px-3 tw-py-2">
                    <FaIcon
                      icon={faPrint}
                      className="tw-h-4 tw-w-4 tw-text-[#1f2937]"
                    />
                    <span className="tw-text-xs tw-font-medium tw-leading-[12px] tw-text-[#1f2937]">
                      {t(option.labelKey)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="tw-flex tw-h-[70px] tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-[#e5e6e6] tw-bg-white tw-px-6">
          {pending && (
            <span className="tw-text-xs tw-text-content-base/70">
              {t('printout.processing-in-progress')}
            </span>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleGenerate}
            loading={pending}
            disabled={selectedReports.size === 0}
            className="tw-h-[38px] tw-w-[130px] tw-rounded-lg tw-text-sm tw-font-medium tw-leading-[14px]"
          >
            {t('printout.generate-reports')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  )
}

export default ViolationsPrintModal
